import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ReviewDialogProps {
  title: string;
  /** Rendered as the dialog's accessible description. */
  description: ReactNode;
  /** While true (a request is in flight) Esc does nothing, so the user can't dismiss mid-submit. */
  busy: boolean;
  onClose: () => void;
  /** Element focused when the dialog opens — e.g. the safe "Cancel" for Approve, the reason field for Reject.
   * Falls back to the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * EPN-104 — the accessible modal used by Payroll Review's Approve and Reject confirmations. Feature-local on
 * purpose (the older, Business Profile Setup `ConfirmDialog` has no Esc handling, focus trap or focus
 * restore, and dismisses on backdrop click). Mounted only while open:
 *  - `role="dialog"` + `aria-modal`, labelled by its title and described by its description;
 *  - focus moves in on open, is trapped (Tab / Shift+Tab wrap), and returns to the opener on close;
 *  - Esc closes it unless `busy`;
 *  - clicking the backdrop does NOT dismiss it (a typed rejection reason must never be lost by a stray click).
 */
export function ReviewDialog({ title, description, busy, onClose, initialFocusRef, children }: ReviewDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Latest props for the document-level key handler without re-subscribing on every render.
  const stateRef = useRef({ busy, onClose });
  useEffect(() => {
    stateRef.current = { busy, onClose };
  });

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const initial = initialFocusRef?.current ?? dialog?.querySelector<HTMLElement>(FOCUSABLE) ?? dialog;
    initial?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (!stateRef.current.busy) {
          event.preventDefault();
          stateRef.current.onClose();
        }
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && document.contains(opener)) opener.focus();
    };
    // Open-time behavior only: the dialog is mounted while open and unmounted when closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    // The backdrop swallows pointer focus changes (so focus can't drift out of the dialog) and has no click handler.
    <div className="payroll-review__backdrop" role="presentation" onMouseDown={(e) => e.preventDefault()}>
      <div
        ref={dialogRef}
        className="payroll-review__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 id={titleId}>{title}</h3>
        <div id={descriptionId} className="payroll-review__dialog-body">
          {description}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
